import React, { useState, useEffect } from "react";
import { 
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  BarChart, Bar, Legend
} from "recharts";
import { customFetch } from '../apiClient';

const DashboardReport = () => {
  const [dashboardData, setDashboardData] = useState({
    stats: { todaySales: 0, monthSales: 0, lowStock: 0, nearExpiry: 0, todaysBills: 0 },
    salesTrend: [],
    topItems: [],
    recentBills: []
  });
  
  const [loading, setLoading] = useState(true);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
     const res = await customFetch('/MedDashboard/summary');
      
      if (res.ok) {
        const data = await res.json();
        setDashboardData(data);
      }
    } catch (err) {
      console.error("Dashboard Error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  const { stats, salesTrend, topItems, recentBills } = dashboardData;

  return (
    <div className="dashboard-container">
      
      {/* Header */}
      <div className="glass-header">
        <div>
          <h2>🚀 Pharmacy Business Overview</h2>
          <p>Real-time analytics & performance</p>
        </div>
        <div className="header-actions">
           <div className="date-badge"><i className="bi bi-calendar3"></i> {new Date().toDateString()}</div>
           <button className="btn-refresh" onClick={fetchDashboard} disabled={loading}>
              <i className={`bi bi-arrow-clockwise ${loading ? 'spin' : ''}`}></i>
           </button>
        </div>
      </div>

      {/* Top Stats Cards */}
      <div className="stats-grid">
        <div className="glass-card green">
          <div className="icon-box"><i className="bi bi-cash-stack"></i></div>
          <div>
            <h3>₹ {(stats?.todaySales || 0).toLocaleString('en-IN', {minimumFractionDigits: 2})}</h3>
            <p>Today's Sales ({stats?.todaysBills || 0} Bills)</p>
          </div>
        </div>

        <div className="glass-card blue">
          <div className="icon-box"><i className="bi bi-graph-up-arrow"></i></div>
          <div>
            <h3>₹ {(stats?.monthSales || 0).toLocaleString('en-IN', {minimumFractionDigits: 2})}</h3>
            <p>This Month's Sales</p>
          </div>
        </div>

        <div className="glass-card red">
          <div className="icon-box"><i className="bi bi-hourglass-split"></i></div>
          <div>
            <h3>{stats?.nearExpiry || 0} Items</h3>
            <p>Expiring within 6 Months</p>
          </div>
        </div>

        <div className="glass-card orange">
          <div className="icon-box"><i className="bi bi-exclamation-triangle"></i></div>
          <div>
            <h3>{stats?.lowStock || 0} Items</h3>
            <p>Low Stock (Need Reorder)</p>
          </div>
        </div>
      </div>

      {/* Middle Section: Charts & Tables */}
      <div className="content-grid">
        
        {/* Sales Trend Chart */}
        <div className="glass-panel chart-box">
          <div className="panel-title">
             <h4><i className="bi bi-activity"></i> Last 7 Days Revenue</h4>
          </div>
          <div style={{height: '280px', width: '100%', marginTop: '10px'}}>
            {salesTrend.length > 0 ? (
              <ResponsiveContainer>
                <AreaChart data={salesTrend} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#4ade80" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#4ade80" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" vertical={false} />
                  <XAxis dataKey="name" stroke="rgba(255,255,255,0.6)" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis stroke="rgba(255,255,255,0.6)" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(val) => `₹${val/1000}k`} />
                  <Tooltip 
                    contentStyle={{backgroundColor: 'rgba(15, 23, 42, 0.9)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', color: '#fff'}}
                    itemStyle={{color: '#4ade80', fontWeight: 'bold'}}
                  />
                  <Area type="monotone" dataKey="sales" name="Revenue" stroke="#4ade80" strokeWidth={3} fillOpacity={1} fill="url(#colorSales)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="empty-chart">No sales data for last 7 days</div>
            )}
          </div>
        </div>

        {/* Top 5 Medicines Bar Chart */}
        <div className="glass-panel chart-box">
          <div className="panel-title">
             <h4><i className="bi bi-star-fill text-warning"></i> Top 5 Medicines (This Month)</h4>
          </div>
          <div style={{height: '280px', width: '100%', marginTop: '10px'}}>
            {topItems.length > 0 ? (
              <ResponsiveContainer>
                <BarChart data={topItems} layout="vertical" margin={{ top: 0, right: 30, left: 40, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" horizontal={true} vertical={false} />
                  <XAxis type="number" stroke="rgba(255,255,255,0.6)" fontSize={11} hide />
                  <YAxis dataKey="name" type="category" stroke="rgba(255,255,255,0.8)" fontSize={11} tickLine={false} axisLine={false} width={100} />
                  <Tooltip 
                    cursor={{fill: 'rgba(255,255,255,0.05)'}}
                    contentStyle={{backgroundColor: 'rgba(15, 23, 42, 0.9)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', color: '#fff'}}
                  />
                  <Bar dataKey="soldQty" name="Sold Qty" fill="#60a5fa" radius={[0, 4, 4, 0]} barSize={20} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="empty-chart">No items sold this month</div>
            )}
          </div>
        </div>

      </div>

      {/* Bottom Section: Recent Bills & Quick Actions */}
      <div className="bottom-grid">
        
        {/* Recent Transactions Table */}
        <div className="glass-panel table-panel">
           <div className="panel-title">
              <h4><i className="bi bi-receipt"></i> Recent Transactions (Today)</h4>
           </div>
           <div className="table-responsive mt-3">
              <table className="dash-table">
                 <thead>
                    <tr>
                       <th>Time</th>
                       <th>Bill No</th>
                       <th>Customer</th>
                       <th className="text-end">Amount</th>
                    </tr>
                 </thead>
                 <tbody>
                    {recentBills.length > 0 ? recentBills.map((bill, idx) => (
                       <tr key={idx}>
                          <td className="text-muted"><i className="bi bi-clock"></i> {bill.time}</td>
                          <td className="fw-bold">{bill.billNo}</td>
                          <td>{bill.customerName}</td>
                          <td className="text-end fw-bold text-success">₹ {bill.amount?.toFixed(2)}</td>
                       </tr>
                    )) : (
                       <tr><td colSpan="4" className="text-center text-muted py-4">No bills generated today.</td></tr>
                    )}
                 </tbody>
              </table>
           </div>
        </div>

        {/* Quick Links */}
        <div className="glass-panel action-panel">
           <div className="panel-title mb-3">
              <h4>⚡ Quick Actions</h4>
           </div>
           <div className="action-buttons-column">
              <a href="/sales" className="glass-action-btn"><div className="act-icon bg-success"><i className="bi bi-cart-plus"></i></div> <span>New POS Bill</span></a>
              <a href="/purchase" className="glass-action-btn"><div className="act-icon bg-primary"><i className="bi bi-truck"></i></div> <span>Add Purchase</span></a>
              <a href="/inventory" className="glass-action-btn"><div className="act-icon bg-warning text-dark"><i className="bi bi-box-seam"></i></div> <span>Check Stock</span></a>
              <a href="/expiry-report" className="glass-action-btn"><div className="act-icon bg-danger"><i className="bi bi-trash3"></i></div> <span>Expiry Actions</span></a>
           </div>
        </div>

      </div>

      {/* ✅ CSS STYLES (Glassmorphism & Medical Theme) */}
      <style jsx>{`
        .dashboard-container {
          padding: 20px;
          min-height: calc(100vh - 60px);
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%); /* Premium Dark Slate Theme */
          color: white;
          font-family: 'Poppins', sans-serif;
        }

        .glass-header {
          display: flex; justify-content: space-between; align-items: center;
          margin-bottom: 25px; padding: 15px 25px;
          background: rgba(255, 255, 255, 0.05); backdrop-filter: blur(10px);
          border-radius: 16px; border: 1px solid rgba(255, 255, 255, 0.1);
        }
        .glass-header h2 { margin: 0; font-size: 22px; font-weight: 700; color: #f8fafc; }
        .glass-header p { margin: 0; opacity: 0.7; font-size: 13px; }
        
        .header-actions { display: flex; align-items: center; gap: 15px; }
        .date-badge { background: rgba(255,255,255,0.1); padding: 8px 16px; border-radius: 8px; font-size: 13px; font-weight: 500; border: 1px solid rgba(255,255,255,0.05); }
        .btn-refresh { background: var(--theme-color, #0088cc); color: white; border: none; padding: 8px 12px; border-radius: 8px; cursor: pointer; transition: 0.3s; }
        .btn-refresh:hover { opacity: 0.9; box-shadow: 0 0 10px var(--theme-color, #0088cc); }
        
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { 100% { transform: rotate(360deg); } }

        .stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 20px; margin-bottom: 25px; }
        
        .glass-card {
          display: flex; align-items: center; gap: 15px; padding: 20px;
          background: rgba(255, 255, 255, 0.03); backdrop-filter: blur(12px);
          border-radius: 16px; border: 1px solid rgba(255, 255, 255, 0.08);
          box-shadow: 0 4px 30px rgba(0, 0, 0, 0.2); transition: transform 0.3s, border-color 0.3s;
        }
        .glass-card:hover { transform: translateY(-5px); }
        .glass-card.green:hover { border-color: #4ade80; }
        .glass-card.blue:hover { border-color: #60a5fa; }
        .glass-card.red:hover { border-color: #f87171; }
        .glass-card.orange:hover { border-color: #fbbf24; }

        .icon-box { width: 55px; height: 55px; border-radius: 14px; display: flex; align-items: center; justify-content: center; font-size: 26px; background: rgba(255, 255, 255, 0.05); }
        .glass-card.green .icon-box { color: #4ade80; background: rgba(74, 222, 128, 0.1); }
        .glass-card.blue .icon-box { color: #60a5fa; background: rgba(96, 165, 250, 0.1); }
        .glass-card.red .icon-box { color: #f87171; background: rgba(248, 113, 113, 0.1); }
        .glass-card.orange .icon-box { color: #fbbf24; background: rgba(251, 191, 36, 0.1); }

        .glass-card h3 { margin: 0; font-size: 24px; font-weight: 700; color: #f8fafc; }
        .glass-card p { margin: 0; font-size: 13px; opacity: 0.6; }

        .content-grid { display: grid; grid-template-columns: 2fr 1fr; gap: 20px; margin-bottom: 25px; }
        .bottom-grid { display: grid; grid-template-columns: 2fr 1fr; gap: 20px; }

        .glass-panel {
          background: rgba(255, 255, 255, 0.03); backdrop-filter: blur(16px);
          border-radius: 16px; border: 1px solid rgba(255, 255, 255, 0.08); padding: 20px;
        }
        .panel-title h4 { margin: 0; font-size: 16px; font-weight: 600; color: #e2e8f0; display: flex; align-items: center; gap: 8px;}
        
        .empty-chart { display: flex; align-items: center; justify-content: center; height: 100%; color: #64748b; font-style: italic; font-size: 13px; }

        /* Custom Table */
        .dash-table { width: 100%; border-collapse: collapse; font-size: 13px; }
        .dash-table th { text-align: left; padding: 12px 10px; color: #94a3b8; font-weight: 600; border-bottom: 1px solid rgba(255,255,255,0.1); text-transform: uppercase; font-size: 11px;}
        .dash-table td { padding: 12px 10px; border-bottom: 1px solid rgba(255,255,255,0.05); color: #e2e8f0; }
        .dash-table tr:hover td { background: rgba(255,255,255,0.02); }

        /* Action Buttons */
        .action-buttons-column { display: flex; flex-direction: column; gap: 12px; }
        .glass-action-btn { 
            display: flex; align-items: center; gap: 15px; padding: 12px 15px; 
            background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08); 
            border-radius: 12px; text-decoration: none; color: #e2e8f0; font-weight: 500; font-size: 14px;
            transition: 0.2s;
        }
        .glass-action-btn:hover { background: rgba(255,255,255,0.08); transform: translateX(5px); color: white; }
        .act-icon { width: 36px; height: 36px; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 18px; color: white; }

        .text-success { color: #4ade80 !important; }
        .text-danger { color: #f87171 !important; }
        .text-warning { color: #fbbf24 !important; }
        .text-muted { color: #94a3b8 !important; }

        @media (max-width: 992px) {
          .content-grid, .bottom-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
};

export default DashboardReport;